(() => {
  if (customElements.get('buddy-sticky-buy')) return;
  const quoteCache = new Map();
  const quoteQuery = `mutation BuddyPrice($input:CartInput!){cartCreate(input:$input){cart{discountCodes{code applicable}discountAllocations{discountedAmount{amount currencyCode}targetType}lines(first:100){nodes{quantity merchandise{... on ProductVariant{id}}cost{totalAmount{amount currencyCode}}}}}userErrors{message}}}`;
  class BuddyStickyBuy extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.pdp = this.closest('buddy-pdp');
      this.form = this.pdp.querySelector('.bp-product-form');
      this.primary = this.form?.querySelector('.bp-add');
      if (!this.form || !this.primary) return;
      this.config = JSON.parse(this.querySelector('[data-sticky-config]').textContent);
      this.button = this.querySelector('.bp-sticky-button');
      this.config.currency ||= window.Shopify?.currency?.active || 'USD';
      this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:this.config.currency});
      this.hidden = false;
      const signal = this.controller.signal;
      this.visibilityObserver = new IntersectionObserver(entries => {
        this.pastHero = entries[0].boundingClientRect.bottom <= 0;
        this.updateVisibility();
      }, {threshold:0});
      this.visibilityObserver.observe(this.pdp.querySelector('.bp-hero'));
      this.resizeObserver = new ResizeObserver(() => {
        document.body.style.setProperty('--bp-sticky-clearance',`${this.offsetHeight + 48}px`);
      });
      this.resizeObserver.observe(this);
      document.body.classList.add('bp-sticky-space');
      this.primaryObserver = new MutationObserver(() => this.syncButton());
      this.primaryObserver.observe(this.primary,{attributes:true,attributeFilter:['disabled','aria-busy'],childList:true,subtree:true,characterData:true});
      const message = this.form.querySelector('.bp-form-message');
      this.messageObserver = new MutationObserver(() => {
        const target = this.querySelector('.bp-sticky-message');
        target.hidden = message.hidden || message.dataset.error !== 'true';
        target.textContent = target.hidden ? '' : message.textContent;
      });
      if (message) this.messageObserver.observe(message,{attributes:true,childList:true,subtree:true,characterData:true});
      this.form.addEventListener('change',()=>this.schedulePrice(),{signal});
      this.form.addEventListener('input',()=>this.schedulePrice(),{signal});
      this.pdp.querySelector('[data-variant-select]')?.addEventListener('change',()=>this.schedulePrice(),{signal});
      this.button.addEventListener('click',() => {
        if (!this.form.reportValidity()) {
          const invalid = this.form.querySelector(':invalid');
          invalid?.scrollIntoView({block:'center',behavior:'smooth'});invalid?.focus({preventScroll:true});
        }
      },{signal});
      window.addEventListener('pageshow',()=>this.schedulePrice(),{signal});
      window.addEventListener('focus',()=>this.schedulePrice(),{signal});
      document.addEventListener('visibilitychange',()=>{if(!document.hidden)this.schedulePrice();},{signal});
      if(typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate,()=>this.schedulePrice());
      }
      const drawer = document.querySelector('cart-drawer, cart-notification');
      if(drawer){
        this.drawerObserver = new MutationObserver(()=>{if(!drawer.classList.contains('active'))this.schedulePrice();});
        this.drawerObserver.observe(drawer,{attributes:true,attributeFilter:['class']});
      }
      this.syncButton();this.schedulePrice();
    }
    disconnectedCallback() {
      this.controller?.abort();this.request?.abort();clearTimeout(this.priceTimer);
      this.visibilityObserver?.disconnect();this.resizeObserver?.disconnect();this.primaryObserver?.disconnect();this.messageObserver?.disconnect();this.drawerObserver?.disconnect();this.unsubscribe?.();
      document.body.classList.remove('bp-sticky-space');document.body.style.removeProperty('--bp-sticky-clearance');
      this.controller = null;
    }
    updateVisibility() {
      this.toggleAttribute('data-visible',Boolean(this.pastHero));
      this.inert = !this.pastHero;
      this.setAttribute('aria-hidden',String(!this.pastHero));
    }
    syncButton() {
      const busy = this.primary.getAttribute('aria-busy') === 'true';
      this.button.disabled = this.primary.disabled;
      this.button.setAttribute('aria-busy',String(busy));
      this.querySelector('[data-sticky-label]').textContent = busy ? this.config.loading : this.primary.disabled ? this.config.soldOut : this.config.label;
    }
    schedulePrice() {
      clearTimeout(this.priceTimer);this.request?.abort();
      this.resetPrice();
      this.priceTimer = setTimeout(()=>this.refreshPrice(),300);
    }
    money(cents){return this.formatter.format(cents/100);}
    selected() {
      const variant = String(this.form.elements.id.value);
      const productVariant = this.config.variants.find(v=>String(v.id)===variant);
      return {variant,price:productVariant?.price ?? this.config.price,quantity:Math.max(1,Number(this.form.elements.quantity.value)||1)};
    }
    resetPrice() {
      const {price,quantity} = this.selected();
      this.querySelector('[data-sticky-price]').textContent = this.money(price);
      this.querySelector('[data-sticky-original]').hidden = true;
      const quantityLabel = this.querySelector('[data-sticky-quantity]');
      quantityLabel.hidden = quantity === 1;quantityLabel.textContent = `each · Qty ${quantity}`;
      this.discountNote('');
    }
    discountNote(text) {
      const note = this.querySelector('[data-sticky-discount]');note.textContent = text;note.hidden = !text;
    }
    async refreshPrice() {
      if(!this.isConnected)return;
      this.request = new AbortController();const {signal} = this.request;
      const timeout = setTimeout(()=>this.request?.signal===signal && this.request.abort(),7000);
      try {
        const root = window.Shopify?.routes?.root || '/';
        let response = await fetch(`${root}cart.js`,{signal,headers:{Accept:'application/json'},cache:'no-store'});
        if(!response.ok)throw new Error('Cart unavailable');
        let cart = await response.json();
        const urlCodes = (new URL(location.href).searchParams.get('discount')||'').split(',').map(v=>v.trim()).filter(Boolean);
        if(!this.appliedUrlCodes && urlCodes.length){
          const codes = [...new Set([...(cart.discount_codes||[]).map(v=>v.code),...urlCodes])];
          response = await fetch(`${root}cart/update.js`,{method:'POST',signal,headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({discount:codes.join(',')})});
          if(!response.ok)throw new Error('Discount unavailable');
          cart = await response.json();this.appliedUrlCodes = true;
        }
        const codes = (cart.discount_codes||[]).map(v=>v.code).filter(Boolean);
        if(!codes.length && !cart.total_discount)return;
        this.discountNote(this.config.discountPending);
        const selected = this.selected();
        if(!Number.isInteger(selected.quantity) || selected.quantity>250 || cart.items.length>=99 || cart.items.some(v=>v.selling_plan_allocation || Object.keys(v.properties||{}).length))return;
        const lines = cart.items.map(item=>({merchandiseId:`gid://shopify/ProductVariant/${item.variant_id}`,quantity:item.quantity,
          ...(item.selling_plan_allocation?{sellingPlanId:`gid://shopify/SellingPlan/${item.selling_plan_allocation.selling_plan.id}`}:{})}));
        lines.push({merchandiseId:`gid://shopify/ProductVariant/${selected.variant}`,quantity:selected.quantity});
        const input = {lines,discountCodes:codes,buyerIdentity:{countryCode:this.config.country}};
        const key = JSON.stringify(input);
        let quote = quoteCache.get(key);
        if(!quote || Date.now()-quote.time>120000){
          response = await fetch('/api/2026-07/graphql.json',{method:'POST',signal,headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({query:quoteQuery,variables:{input}})});
          if(!response.ok)throw new Error('Price unavailable');
          const result = await response.json();const data=result.data?.cartCreate;
          if(result.errors?.length || data?.userErrors?.length || !data?.cart)throw new Error('Price unavailable');
          quote = {time:Date.now(),cart:data.cart};quoteCache.set(key,quote);
        }
        if(signal.aborted || !this.isConnected)return;
        const q=quote.cart, applicable=q.discountCodes.filter(v=>v.applicable).map(v=>v.code);
        if(codes.length && !applicable.length)return;
        const quotedLines=q.lines.nodes;
        if(quotedLines.reduce((sum,v)=>sum+v.quantity,0)!==cart.item_count+selected.quantity)return;
        if(quotedLines.some(line=>line.cost.totalAmount.currencyCode!==cart.currency) || cart.currency!==this.config.currency)return;
        const orderSaving=q.discountAllocations.filter(v=>v.targetType==='LINE_ITEM').reduce((sum,v)=>sum+Math.round(Number(v.discountedAmount.amount)*100),0);
        const currentId=`gid://shopify/ProductVariant/${selected.variant}`;
        const singleProduct=quotedLines.length>0 && quotedLines.every(v=>v.merchandise.id===currentId) && !cart.items.some(v=>v.selling_plan_allocation);
        const codeLabel=applicable.join(' + ');
        const welcomeCopy=applicable.length===1 && applicable[0].toUpperCase()==='WELCOME10' ? 'Use code WELCOME10 for $10 off your first order' : null;
        if(singleProduct){
          const quantity=quotedLines.reduce((sum,v)=>sum+v.quantity,0);
          const lineTotal=quotedLines.reduce((sum,v)=>sum+Math.round(Number(v.cost.totalAmount.amount)*100),0);
          const unitPrice=Math.max(0,Math.round((lineTotal-orderSaving)/quantity));
          if(unitPrice<selected.price){
            const original=this.querySelector('[data-sticky-original]');original.textContent=this.money(selected.price);original.hidden=false;
            this.querySelector('[data-sticky-price]').textContent=this.money(unitPrice);
            this.discountNote(welcomeCopy || (codeLabel?`With ${codeLabel}${quantity>1?' · per item':''}`:'Discount applied'));
          }else this.discountNote(codeLabel?`${codeLabel} · checked in cart`:this.config.discountPending);
        }else{
          this.discountNote(orderSaving>0?(welcomeCopy || `${codeLabel?codeLabel+' · ':''}${this.money(orderSaving)} off order`):this.config.discountPending);
        }
      }catch(error){
        if(error.name!=='AbortError')this.discountNote(this.querySelector('[data-sticky-discount]').hidden?'':this.config.discountPending);
      }finally{clearTimeout(timeout);}
    }
  }
  customElements.define('buddy-sticky-buy',BuddyStickyBuy);
})();
